const {
  createOrUpdateGrade,
  getGradesByClass,
  getBoletimByStudent,
  setGradesCaption,
  getGradesCaption,
} = require('../api/endpoints/grades');

/**
 * Service para operações relacionadas a notas
 */
class GradesService {
  /**
   * Cria ou atualiza uma nota
   * @param {Object} gradeData - Dados da nota
   * @returns {Promise<Object>} Resultado da operação
   */
  async createOrUpdateGrade(gradeData) {
    return await createOrUpdateGrade(gradeData);
  }

  /**
   * Obtém notas por turma, disciplina e período
   * @param {number} TurmaID - ID da turma
   * @param {number} DisciplinaID - ID da disciplina
   * @param {number} period - Período (1-4)
   * @returns {Promise<Array>} Lista de notas
   */
  async getGradesByClassroom(TurmaID, DisciplinaID, period) {
    if (!period) {
      throw new Error('Período é obrigatório');
    }

    const grades = await getGradesByClass({
      TurmaID,
      DisciplinaID,
      period,
    });

    return grades;
  }

  /**
   * Obtém o boletim de um aluno
   * @param {number} AlunoID - ID do aluno
   * @param {number} TurmaID - ID da turma
   * @returns {Promise<Object>} Boletim do aluno
   */
  async getBoletim(AlunoID, TurmaID) {
    return await getBoletimByStudent(AlunoID, TurmaID);
  }

  /**
   * Define legenda de notas para uma turma e período
   * @param {number} TurmaID - ID da turma
   * @param {number} period - Período (1-4)
   * @param {string} caption - Texto da legenda
   * @returns {Promise<Object>} Resultado da operação
   */
  async setCaption(TurmaID, period, caption) {
    return await setGradesCaption({
      TurmaID,
      period,
      caption,
    });
  }

  /**
   * Obtém legenda de notas
   * @param {number} TurmaID - ID da turma
   * @param {number} period - Período (1-4)
   * @returns {Promise<Object>} Legenda de notas
   */
  async getCaption(TurmaID, period) {
    return await getGradesCaption(TurmaID, period);
  }

  /**
   * Formata valor de nota para exibição
   * @param {string|number} value - Valor da nota
   * @returns {string} Nota formatada
   */
  formatGradeValue(value) {
    if (value === null || value === undefined || value === '' || value === '0') {
      return '';
    }

    try {
      const numValue = parseFloat(String(value).replace(',', '.'));
      if (isNaN(numValue) || numValue === 0) {
        return '';
      }

      const roundedValue = Math.round(numValue * 10) / 10;
      return roundedValue.toFixed(1).replace('.', ',');
    } catch (error) {
      console.error('Erro ao formatar nota:', error);
      return String(value);
    }
  }

  /**
   * Calcula AC1 (soma de AT1 e AT2)
   * @param {string} at1 - Nota AT1
   * @param {string} at2 - Nota AT2
   * @returns {string} AC1 calculado
   */
  calculateAC1(at1, at2) {
    const at1Value = parseFloat((at1 && at1.replace(',', '.')) || 0);
    const at2Value = parseFloat((at2 && at2.replace(',', '.')) || 0);

    if (at1Value === 0 && at2Value === 0) return '';

    const sum = at1Value + at2Value;
    const roundedSum = Math.round(sum * 10) / 10;
    return roundedSum.toFixed(1).replace('.', ',');
  }

  /**
   * Calcula AC2 (soma de AT3, AT4, AT5 e AT6)
   * @param {string} at3 - Nota AT3
   * @param {string} at4 - Nota AT4
   * @param {string} at5 - Nota AT5
   * @param {string} at6 - Nota AT6
   * @returns {string} AC2 calculado
   */
  calculateAC2(at3, at4, at5, at6) {
    const at3Value = parseFloat((at3 && at3.replace(',', '.')) || 0);
    const at4Value = parseFloat((at4 && at4.replace(',', '.')) || 0);
    const at5Value = parseFloat((at5 && at5.replace(',', '.')) || 0);
    const at6Value = parseFloat((at6 && at6.replace(',', '.')) || 0);

    if (at3Value === 0 && at4Value === 0 && at5Value === 0 && at6Value === 0) {
      return '';
    }

    const sum = at3Value + at4Value + at5Value + at6Value;
    const roundedSum = Math.round(sum * 10) / 10;
    return roundedSum.toFixed(1).replace('.', ',');
  }

  /**
   * Calcula MB (média de AC1 e AC2)
   * @param {string} ac1 - Nota AC1
   * @param {string} ac2 - Nota AC2
   * @returns {string} MB calculado
   */
  calculateMB(ac1, ac2) {
    if ((!ac1 || ac1 === '') && (!ac2 || ac2 === '')) return '';

    const ac1Value = parseFloat((ac1 && ac1.replace(',', '.')) || 0);
    const ac2Value = parseFloat((ac2 && ac2.replace(',', '.')) || 0);

    if (ac1Value === 0 && ac2Value === 0) return '';

    const media = (ac1Value + ac2Value) / 2;
    const roundedMedia = Math.round(media * 10) / 10;
    return roundedMedia.toFixed(1).replace('.', ',');
  }
}

// Exportar instância singleton
const gradesService = new GradesService();
module.exports = gradesService;

